import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, UserPlus, UserMinus, X, UserX, ArrowRightLeft } from "lucide-react";
import ReplaceMemberDialog from "./replace-member-dialog";

type ManageTeamMembersDialogProps = {
  teamId: number | null;
  teamName?: string;
  isOpen: boolean;
  onClose: () => void;
};

type TeamMember = {
  employeeId: number;
  teamId: number;
  id: number;
};

type Employee = {
  id: number;
  fullName: string;
  email: string;
  jobTitle?: string;
  department?: string;
  status?: string;
};

export default function ManageTeamMembersDialog({
  teamId,
  teamName = "Team",
  isOpen,
  onClose,
}: ManageTeamMembersDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);
  const [employeeToReplace, setEmployeeToReplace] = useState<{ id: number; name: string } | null>(null);

  // Debug logging for dialog props

  // Fetch all employees for selection
  const { data: allEmployees = [], isLoading: isLoadingEmployees } = useQuery<Employee[]>({
    queryKey: ['/api/employees'],
    enabled: isOpen,
  });

  // Fetch current team members
  const { data: currentTeamMembers = [], isLoading: isLoadingTeamMembers } = useQuery<TeamMember[]>({
    queryKey: ['/api/teams', teamId, 'members'],
    enabled: isOpen && teamId !== null,
    queryFn: async () => {
      if (!teamId) return [];
      try {
        const response = await apiRequest(`/api/teams/${teamId}/members`);
        return response;
      } catch (error) {
        console.error("Error fetching team members:", error);
        return [];
      }
    },
  });

  // Get IDs of current team members
  const currentMemberIds = currentTeamMembers.map((member) => member.employeeId);

  // Separate state for add and remove tabs
  const [employeesToAdd, setEmployeesToAdd] = useState<number[]>([]);
  const [employeesToRemove, setEmployeesToRemove] = useState<number[]>([]);
  const [activeTab, setActiveTab] = useState<string>("add");

  // Reset selections when dialog opens or team changes
  useEffect(() => {
    if (isOpen) {
      setEmployeesToAdd([]);
      setEmployeesToRemove([]);
      setActiveTab("add");
    }
  }, [isOpen, teamId]);

  // Filter employees for Add tab (not current members)
  const availableEmployees = allEmployees.filter((employee) => {
    const isNotMember = !currentMemberIds.includes(employee.id);
    const matchesSearch =
      searchQuery === "" ||
      employee.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      employee.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (employee.jobTitle && employee.jobTitle.toLowerCase().includes(searchQuery.toLowerCase()));
    
    return isNotMember && matchesSearch;
  });

  // Filter current team members for Remove tab
  const currentMembers = allEmployees.filter((employee) => {
    const isMember = currentMemberIds.includes(employee.id);
    const matchesSearch =
      searchQuery === "" ||
      employee.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      employee.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (employee.jobTitle && employee.jobTitle.toLowerCase().includes(searchQuery.toLowerCase()));
    
    return isMember && matchesSearch;
  });

  // Debug logging
  useEffect(() => {
    if (isOpen && teamId) {
    }
  }, [isOpen, teamId, allEmployees, currentTeamMembers, currentMemberIds, availableEmployees, currentMembers, teamName]);

  // Handle employee selection for adding
  const toggleEmployeeForAdd = (employeeId: number) => {
    if (employeesToAdd.includes(employeeId)) {
      setEmployeesToAdd(employeesToAdd.filter((id) => id !== employeeId));
    } else {
      setEmployeesToAdd([...employeesToAdd, employeeId]);
    }
  };

  // Handle employee selection for removal
  const toggleEmployeeForRemove = (employeeId: number) => {
    if (employeesToRemove.includes(employeeId)) {
      setEmployeesToRemove(employeesToRemove.filter((id) => id !== employeeId));
    } else {
      setEmployeesToRemove([...employeesToRemove, employeeId]);
    }
  };

  // Save changes mutation
  const saveChangesMutation = useMutation({
    mutationFn: async () => {
      if (!teamId) throw new Error("Team ID is required");
      
      const promises = [];
      
      // Add new members
      if (employeesToAdd.length > 0) {
        promises.push(
          apiRequest(`/api/teams/${teamId}/members`, { 
            method: "POST", 
            body: { employeeIds: employeesToAdd }
          })
        );
      }
      
      // Remove members
      for (const employeeId of employeesToRemove) {
        promises.push(
          apiRequest(`/api/teams/${teamId}/members/${employeeId}`, {
            method: "DELETE"
          })
        );
      }
      
      return Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/teams', teamId, 'members'] });
      queryClient.invalidateQueries({ queryKey: ['/api/teams'] });
      toast({
        title: "Team members updated",
        description: "The team members have been updated successfully.",
      });
      onClose();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update team members. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleSaveChanges = () => {
    saveChangesMutation.mutate();
  };

  const handleReplaceEmployee = (employee: Employee) => {
    setEmployeeToReplace({ id: employee.id, name: employee.fullName });
    setReplaceDialogOpen(true);
  };

  const handleCloseReplaceDialog = () => {
    setReplaceDialogOpen(false);
    setEmployeeToReplace(null);
  };

  const isLoading = isLoadingEmployees || isLoadingTeamMembers;
  const isPending = saveChangesMutation.isPending;
  const hasChanges = employeesToAdd.length > 0 || employeesToRemove.length > 0;

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Manage Team Members: {teamName}</DialogTitle>
          <DialogDescription>
            Add new members or remove existing members from this team. Check/uncheck to add or remove members.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-2 flex-shrink-0">
            <TabsTrigger value="add" className="flex items-center gap-2">
              <UserPlus className="h-4 w-4" />
              Add Members ({employeesToAdd.length})
            </TabsTrigger>
            <TabsTrigger value="remove" className="flex items-center gap-2">
              <UserMinus className="h-4 w-4" />
              Remove Members ({employeesToRemove.length})
            </TabsTrigger>
          </TabsList>

          <div className="relative mb-4 mt-4 flex-shrink-0">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search employees..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          <TabsContent value="add" className="flex-1 mt-0 min-h-0">
            <ScrollArea className="h-full max-h-[300px]">
              {isLoading ? (
                <div className="flex items-center justify-center p-8">
                  <LoadingSpinner className="h-6 w-6" />
                </div>
              ) : availableEmployees.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {allEmployees.length === currentMemberIds.length ? 
                    "All employees are already team members" : 
                    "No available employees match your search"}
                </div>
              ) : (
                <div className="space-y-2">
                  {availableEmployees.map((employee) => {
                    const isSelected = employeesToAdd.includes(employee.id);
                    
                    return (
                      <div
                        key={employee.id}
                        className={`flex items-center justify-between p-3 rounded-md ${
                          isSelected ? "bg-green-50 border border-green-200" : "hover:bg-muted"
                        }`}
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleEmployeeForAdd(employee.id)}
                            id={`add-employee-${employee.id}`}
                          />
                          <div className="flex-1">
                            <label
                              htmlFor={`add-employee-${employee.id}`}
                              className="text-sm font-medium cursor-pointer"
                            >
                              {employee.fullName}
                            </label>
                            <div className="text-xs text-muted-foreground">
                              {employee.email}
                              {employee.jobTitle && ` • ${employee.jobTitle}`}
                            </div>
                            {isSelected && (
                              <div className="text-xs mt-1">
                                <span className="text-green-600 font-medium">
                                  ✓ Will be added to team
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {isSelected && (
                          <div className="text-xs bg-green-100 text-green-700 rounded-full px-2 py-1 font-medium">
                            Add
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          <TabsContent value="remove" className="flex-1 mt-0 min-h-0">
            <ScrollArea className="h-full max-h-[300px]">
              {isLoading ? (
                <div className="flex items-center justify-center p-8">
                  <LoadingSpinner className="h-6 w-6" />
                </div>
              ) : currentMembers.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {currentMemberIds.length === 0 ? 
                    "No members in this team yet" : 
                    "No current members match your search"}
                </div>
              ) : (
                <div className="space-y-2">
                  {currentMembers.map((employee) => {
                    const isSelected = employeesToRemove.includes(employee.id);
                    
                    return (
                      <div
                        key={employee.id}
                        className={`flex items-center justify-between p-3 rounded-md ${
                          isSelected ? "bg-red-50 border border-red-200" : "bg-blue-50 border border-blue-200"
                        }`}
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleEmployeeForRemove(employee.id)}
                            id={`remove-employee-${employee.id}`}
                          />
                          <div className="flex-1">
                            <label
                              htmlFor={`remove-employee-${employee.id}`}
                              className="text-sm font-medium cursor-pointer"
                            >
                              {employee.fullName}
                            </label>
                            <div className="text-xs text-muted-foreground">
                              {employee.email}
                              {employee.jobTitle && ` • ${employee.jobTitle}`}
                            </div>
                            <div className="text-xs mt-1">
                              {isSelected ? (
                                <span className="text-red-600 font-medium">
                                  ✓ Will be removed from team
                                </span>
                              ) : (
                                <span className="text-blue-600 font-medium">
                                  Currently a team member
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {!isSelected && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleReplaceEmployee(employee)}
                              className="h-8 px-2 text-xs"
                              data-testid={`button-replace-${employee.id}`}
                            >
                              <ArrowRightLeft className="h-3 w-3 mr-1" />
                              Replace
                            </Button>
                          )}
                          <div className="text-xs rounded-full px-2 py-1 font-medium">
                            {isSelected ? (
                              <div className="bg-red-100 text-red-700">Remove</div>
                            ) : (
                              <div className="bg-blue-100 text-blue-700">Member</div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>

        <DialogFooter className="pt-4 border-t mt-4 flex-shrink-0">
          <div className="flex justify-between items-center w-full">
            <div className="text-sm text-muted-foreground">
              {employeesToAdd.length > 0 && (
                <span>{employeesToAdd.length} to add</span>
              )}
              {employeesToAdd.length > 0 && employeesToRemove.length > 0 && " • "}
              {employeesToRemove.length > 0 && (
                <span>{employeesToRemove.length} to remove</span>
              )}
              {!hasChanges && <span>No changes</span>}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} disabled={isPending}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveChanges}
                disabled={!hasChanges || isPending}
                className="gap-1"
              >
                {isPending ? (
                  <LoadingSpinner className="h-4 w-4" />
                ) : (
                  <>
                    <UserPlus className="h-4 w-4" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>

      {/* Replace Member Dialog */}
      <ReplaceMemberDialog
        type="team"
        teamId={teamId}
        teamName={teamName}
        fromEmployeeId={employeeToReplace?.id || null}
        fromEmployeeName={employeeToReplace?.name || ""}
        isOpen={replaceDialogOpen}
        onClose={handleCloseReplaceDialog}
      />
    </Dialog>
  );
}